(() => {
  if (window.__cloudSearchPasteFixInit) return;
  window.__cloudSearchPasteFixInit = true;

  const INPUT_SELECTOR = 'input[name="q"], input.search__input';
  const RESULTS_SELECTOR = '#cloud-search-results, .cloud-search-results';

  function getInput() {
    return document.querySelector(INPUT_SELECTOR);
  }

  function fireInput(input) {
    if (!input) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /*
   * Pasting drops the whole query in a single event. Cloud Search's instant
   * search occasionally mishandles that: it renders the hover preview but no
   * result rows, leaving a detached preview card and a "0 results" panel.
   * Re-firing `input` once the pasted value has settled forces a clean
   * re-query, the same way incremental typing would.
   */
  document.addEventListener(
    'paste',
    (event) => {
      const target = event.target;
      const input = target && target.closest ? target.closest(INPUT_SELECTOR) : null;
      if (!input) return;
      requestAnimationFrame(() => requestAnimationFrame(() => fireInput(input)));
      setTimeout(() => fireInput(input), 250);
    },
    true
  );

  /*
   * Safety net: if the panel settles with a visible preview but no visible
   * result rows, re-query to recover. Two broken states are covered:
   *
   *   1. No dt.cs-item in the DOM at all (original paste-race case).
   *   2. dt.cs-item rows exist in the DOM but the list container has
   *      collapsed to zero height (rows are present but invisible) — this
   *      can happen when Cloud Search resets its panel height mid-render or
   *      when a rapid-query race leaves the container in an inconsistent
   *      state. The CSS `not(:has(dt.cs-item))` rule does not catch this.
   *
   * recoveredFor is cleared whenever the input value changes so the same
   * query can be recovered again if the problem recurs.
   */
  const recoveredFor = new WeakMap();
  const timers = new WeakMap();

  function hasVisibleRows(root) {
    const rows = root.querySelectorAll('dt.cs-item');
    if (!rows.length) return false;

    // Rows are in the DOM — check whether their scroll container is
    // actually rendered with non-zero height so they are truly visible.
    const list = root.querySelector('.cloud-search-results__list');
    if (list && list.getBoundingClientRect().height < 1) return false;

    return true;
  }

  function isBroken(root) {
    if (root.hasAttribute('hidden')) return false;
    if (hasVisibleRows(root)) return false;
    return [...root.querySelectorAll('dd')].some((dd) => {
      const style = getComputedStyle(dd);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  function scheduleCheck(root) {
    clearTimeout(timers.get(root));
    timers.set(
      root,
      setTimeout(() => {
        if (!isBroken(root)) return;
        const input = getInput();
        const value = input ? input.value.trim() : '';
        if (!value || recoveredFor.get(root) === value) return;
        recoveredFor.set(root, value);
        fireInput(input);
      }, 200)
    );
  }

  function bind(root) {
    if (!root || root.dataset.pasteFixBound) return;
    root.dataset.pasteFixBound = 'true';
    scheduleCheck(root);
    new MutationObserver(() => scheduleCheck(root)).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'style', 'class'],
    });
  }

  function clearRecoveryCache() {
    document.querySelectorAll(RESULTS_SELECTOR).forEach((root) => {
      recoveredFor.delete(root);
    });
  }

  function init() {
    // Clear recovery cache on each new input event so repeated broken states
    // for the same query can still trigger recovery.
    document.addEventListener('input', (e) => {
      if (e.target && e.target.closest && e.target.closest(INPUT_SELECTOR)) {
        clearRecoveryCache();
      }
    }, true);

    document.querySelectorAll(RESULTS_SELECTOR).forEach(bind);
    new MutationObserver(() => {
      document.querySelectorAll(RESULTS_SELECTOR).forEach(bind);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
